import { useEffect, useState } from 'react';
import { isPendingState } from '@/lib/meta-popup-context';

interface FacebookLoginToken {
  accessToken: string;
  state: string;
}

const RELAY_KEY = 'fb_login_token_relay';
/** Tokens older than 30 minutes are considered stale and ignored. */
const RELAY_TTL_MS = 30 * 60 * 1000;

/**
 * Receives FB_LOGIN_TOKEN postMessage + localStorage storage/mount events from
 * the /instagram/facebook-login/callback popup page and exposes the token for
 * the parent modal to consume in the page-picker step.
 *
 * Handles three relay paths:
 *  - postMessage (normal popup with intact opener) — same-origin guard applied
 *  - storage event (broken-opener / cross-tab case)
 *  - on-mount localStorage read (same-tab mobile direct-nav case — storage
 *    events do NOT fire in the same tab that called setItem)
 *
 * Call `clear()` after consuming the token to reset state for a potential retry.
 */
export function useFacebookLoginToken(): {
  token: FacebookLoginToken | null;
  clear: () => void;
} {
  const [token, setToken] = useState<FacebookLoginToken | null>(null);

  useEffect(() => {
    function consumePayload(raw: string): boolean {
      try {
        const payload = JSON.parse(raw) as {
          accessToken?: string;
          state?: string;
          ts?: number;
        };
        if (!payload?.accessToken || !payload?.state) return false;
        // Only the tab that launched the dialog owns this relay; other open
        // dashboard tabs see the same storage event and must not consume it.
        if (!isPendingState(payload.state)) return false;
        // Reject stale relay entries (> 30 min old).
        if (payload.ts && Date.now() - payload.ts > RELAY_TTL_MS) {
          localStorage.removeItem(RELAY_KEY);
          return false;
        }
        setToken({ accessToken: payload.accessToken, state: payload.state });
        localStorage.removeItem(RELAY_KEY);
        return true;
      } catch {
        return false;
      }
    }

    // On-mount check: the mobile path (same-tab direct-nav) writes to
    // localStorage and navigates to the dashboard. The storage event does NOT
    // fire in the same tab, so we read on mount instead.
    const existing = localStorage.getItem(RELAY_KEY);
    if (existing) {
      consumePayload(existing);
    }

    function handleMessage(event: MessageEvent) {
      // Same-origin guard — reject cross-origin postMessages.
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'FB_LOGIN_TOKEN') return;
      if (!event.data.accessToken || !event.data.state) return;
      setToken({ accessToken: event.data.accessToken, state: event.data.state });
    }

    function handleStorage(event: StorageEvent) {
      if (event.key !== RELAY_KEY || !event.newValue) return;
      consumePayload(event.newValue);
    }

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return { token, clear: () => setToken(null) };
}
